import React, { useState, useRef, useCallback } from 'react';
import { Mic, Square, Play, Pause, Upload, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

interface VoiceRecorderProps {
  onVoiceCapture: (file: File) => Promise<void>;
  disabled?: boolean;
}

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onVoiceCapture,
  disabled = false,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [duration, setDuration] = useState(0);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  const { toast } = useToast();

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      setDuration(0);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(blob);
        setRecordedAudio(audioUrl);
        
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setIsPaused(false);

      // Start duration counter
      intervalRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast({
        title: "Microphone Error",
        description: "Unable to access microphone. Please check permissions.",
        variant: "destructive",
      });
    }
  }, [toast]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [isRecording]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
        
        intervalRef.current = setInterval(() => {
          setDuration(prev => prev + 1);
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
        
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }
  }, [isRecording, isPaused]);

  const handleUpload = async () => {
    if (!recordedAudio) return;

    setUploading(true);
    try {
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      const file = new File([blob], `voice_note_${Date.now()}.webm`, { type: 'audio/webm' });
      
      await onVoiceCapture(file);
      
      // Clean up
      URL.revokeObjectURL(recordedAudio);
      setRecordedAudio(null);
      chunksRef.current = [];
      setDuration(0);
      
      toast({
        title: "Success",
        description: "Voice note uploaded successfully!",
      });
    } catch (error) {
      console.error('Error uploading voice note:', error);
      toast({
        title: "Upload Error",
        description: "Failed to upload voice note.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const deleteRecording = () => {
    if (recordedAudio) {
      URL.revokeObjectURL(recordedAudio);
      setRecordedAudio(null);
    }
    chunksRef.current = [];
    setDuration(0);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      <div className="bg-muted rounded-lg p-6 text-center">
        {isRecording && (
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-3 h-3 bg-destructive rounded-full animate-pulse" />
            <span className="text-sm font-medium text-muted-foreground">
              {isPaused ? 'PAUSED' : 'RECORDING'}
            </span>
          </div>
        )}
        
        <div className="text-2xl font-mono font-bold mb-4">
          {formatDuration(duration)}
        </div>
        
        {recordedAudio && (
          <audio
            src={recordedAudio}
            controls
            className="w-full max-w-md mx-auto"
          />
        )}
      </div>

      <div className="flex flex-col gap-1">
        {!recordedAudio ? (
          <>
            {!isRecording ? (
              <Button
                onClick={startRecording}
                disabled={disabled}
                variant="default"
                size="sm"
                className="w-full text-xs"
              >
                <Mic className="w-3 h-3 mr-1" />
                Record
              </Button>
            ) : (
              <div className="flex gap-1">
                <Button
                  onClick={pauseRecording}
                  disabled={disabled}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  {isPaused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
                </Button>
                <Button
                  onClick={stopRecording}
                  disabled={disabled}
                  variant="destructive"
                  size="sm"
                  className="flex-1"
                >
                  <Square className="w-3 h-3" />
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <Button
              onClick={handleUpload}
              disabled={disabled || uploading}
              variant="default"
              size="sm"
              className="w-full text-xs"
            >
              {uploading ? (
                <>
                  <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></span>
                  Upload
                </>
              ) : (
                <>
                  <Upload className="w-3 h-3 mr-1" />
                  Upload
                </>
              )}
            </Button>
            <div className="flex gap-1">
              <Button
                onClick={deleteRecording}
                disabled={disabled || uploading}
                variant="outline"
                size="sm"
                className="flex-1 text-xs"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Delete
              </Button>
              <Button
                onClick={startRecording}
                disabled={disabled || uploading}
                variant="outline"
                size="sm"
                className="flex-1 text-xs"
              >
                <Mic className="w-3 h-3 mr-1" />
                Again
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VoiceRecorder;